import { useState, useEffect } from 'react'
import { X, DollarSign, Loader2 } from 'lucide-react'
import { useDropCash } from '@/api/auth'
import { useSessionStore } from '@/stores/sessionStore'
import { useUIStore } from '@/stores/uiStore'

interface DropCashModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DENOMINATIONS = [
  { label: '$100', value: 100 },
  { label: '$50', value: 50 },
  { label: '$20', value: 20 },
  { label: '$10', value: 10 },
  { label: '$5', value: 5 },
  { label: '$2', value: 2 },
  { label: '$1', value: 1 },
  { label: '$0.25', value: 0.25 },
  { label: '$0.10', value: 0.10 },
  { label: '$0.05', value: 0.05 },
  { label: '$0.01', value: 0.01 },
]

export function DropCashModal({ open, onOpenChange }: DropCashModalProps) {
  const { staff } = useSessionStore()
  const { showToast } = useUIStore()
  const dropCash = useDropCash()

  const [quantities, setQuantities] = useState<Record<number, number>>({})

  useEffect(() => {
    if (open) {
      setQuantities({})
    }
  }, [open])

  const handleQuantityChange = (value: number, qty: string) => {
    const quantity = parseInt(qty) || 0
    setQuantities(prev => ({
      ...prev,
      [value]: quantity,
    }))
  }

  const calculateTotal = () => {
    return Object.entries(quantities).reduce((sum, [value, qty]) => {
      return sum + (parseFloat(value) * qty)
    }, 0)
  }

  const handleSubmit = async () => {
    const total = calculateTotal()
    
    if (total <= 0) {
      showToast({
        type: 'error',
        title: 'Invalid Amount',
        message: 'Please enter a valid amount to drop',
      })
      return
    }

    const moneys = DENOMINATIONS.map(denom => ({
      description: denom.label,
      value: denom.value,
      qty: quantities[denom.value] || 0,
    })).filter(m => m.qty > 0)

    try {
      await dropCash.mutateAsync({
        amount: total,
        cashOut: { moneys },
      })
      
      showToast({
        type: 'success',
        title: 'Drop Cash',
        message: `Successfully dropped $${total.toFixed(2)} from drawer`,
      })
      
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to drop cash:', error)
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to drop cash',
      })
    }
  }

  if (!open) return null

  const total = calculateTotal()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">Drop Cash</h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            disabled={dropCash.isPending}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {staff?.full_name} - {new Date().toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Count the cash to drop from your drawer
            </p>
          </div>

          <div className="space-y-2">
            {DENOMINATIONS.map((denom) => (
              <div key={denom.value} className="flex items-center gap-3">
                <div className="w-16 text-right font-medium text-gray-700">
                  {denom.label}
                </div>
                <span className="text-gray-400">×</span>
                <input
                  type="number"
                  min="0"
                  value={quantities[denom.value] || ''}
                  onChange={(e) => handleQuantityChange(denom.value, e.target.value)}
                  className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:ring-0 outline-none text-center"
                  placeholder="0"
                />
                <span className="text-gray-400">=</span>
                <div className="w-24 text-right font-medium text-gray-900">
                  ${((quantities[denom.value] || 0) * denom.value).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t-2 border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-700">Total to drop:</span>
              <span className="text-2xl font-bold text-orange-600">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-5 border-t">
          <button
            onClick={() => onOpenChange(false)}
            disabled={dropCash.isPending}
            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={dropCash.isPending || total <= 0}
            className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {dropCash.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Drop Cash
          </button>
        </div>
      </div>
    </div>
  )
}
